import { Tooltip, IconButton, Typography } from '@mui/material';
import { useCopyToClipboard } from 'minimal-shared/hooks';
import { useCallback } from 'react';
import { Iconify } from '../iconify';
import { toast } from 'src/components/snackbar';
import { useTranslate } from 'src/locales';
import type { Variant } from '@mui/material/styles/createTypography';

type OwnProps = {
  value?: string;
  hasTooltip?: boolean;
  tooltipText?: string;
  hasCopy?: boolean;
  forceCopy?: boolean;
  maxLength?: number;
  typography?: Variant;
  dataTestId?: string;
  toTruncate?: boolean;
  truncateLength?: number;
  styles?: object;
  tooltipOffset?: [number?, number?];
};

const DEFAULT_MAX_LENGTH = 11;

const ShortenCode = ({
  value,
  hasTooltip = true,
  tooltipText,
  hasCopy = true,
  maxLength = DEFAULT_MAX_LENGTH,
  typography = 'body1',
  forceCopy = false,
  dataTestId,
  toTruncate = false,
  truncateLength = 0,
  styles = {},
  tooltipOffset = [],
}: OwnProps) => {
  const { t } = useTranslate();
  const { copy } = useCopyToClipboard();

  const onCopy = useCallback(
    (e: any, text: string) => {
      if (text) {
        e.preventDefault();
        e.stopPropagation();

        toast.success(t('common.valueCopied'), { position: 'bottom-right' });
        copy(text);
      }
    },
    [copy]
  );

  if (!value) {
    return null;
  }

  const renderCopyButton = () => {
    return (
      <IconButton
        data-test-id="copy-btn"
        color="default"
        sx={{ borderRadius: 0, p: 0, ml: 1 }}
        onClick={(e) => onCopy(e, value)}
      >
        <Iconify icon="lucide:copy" width={16} />
      </IconButton>
    );
  };

  const renderText = () => {
    return (
      <Typography
        variant={typography}
        sx={
          toTruncate
            ? {
                wordBreak: 'break-all',
                overflowWrap: 'anywhere',
                whiteSpace: 'normal',
                ...styles,
              }
            : {
                ...styles,
              }
        }
        {...(dataTestId ? { 'data-test-id': dataTestId } : {})}
      >
        {toTruncate
          ? value.length > truncateLength
            ? value.slice(0, truncateLength) + '...'
            : value
          : shortenString(value, maxLength)}

        {((hasCopy && value.length > maxLength) || forceCopy) && renderCopyButton()}
      </Typography>
    );
  };

  return (
    <>
      {hasTooltip && (
        <>
          {value.length > maxLength && (
            <Tooltip
              title={tooltipText || value}
              slotProps={{
                tooltip: {
                  ...({
                    'data-test-id': 'tooltip-content',
                  } as React.HTMLAttributes<HTMLDivElement>),
                },
                ...(tooltipOffset.length === 2
                  ? {
                      popper: {
                        modifiers: [
                          {
                            name: 'offset',
                            options: {
                              offset: tooltipOffset,
                            },
                          },
                        ],
                      },
                    }
                  : {}),
              }}
            >
              {renderText()}
            </Tooltip>
          )}

          {value.length <= maxLength && renderText()}
        </>
      )}

      {!hasTooltip && renderText()}
    </>
  );
};

export default ShortenCode;

const shortenString = (value: string, maxLength: number): string => {
  if (value.length <= maxLength) {
    return value;
  }

  const firstPart = value.slice(0, maxLength / 2 - 1);
  const lastPart = value.slice(-(maxLength / 2) + 1);

  return `${firstPart}...${lastPart}`;
};
